import React, { ReactElement, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { SCDatePickerWrapper } from './styles';
import { ITSDatePickerProps } from './types';

/**
 * TSDatePicker - A wrapper around the MUI DatePicker component
 * Provides consistent styling and API across the application
 * Supports Faker Filler and other autofill extensions
 */
const TSDatePicker = ({
    ariaLabel,
    clearable = true,
    dataTestId,
    defaultValue,
    disableFuture,
    disablePast,
    errorText,
    format = 'MM/DD/YYYY',
    helperText,
    id,
    isDisabled,
    isRequired,
    label,
    maxDate,
    minDate,
    name,
    onAccept,
    onBlur,
    onChange,
    onError,
    placeholder,
    readOnly,
    value,
    variant = 'standard',
    views
}: ITSDatePickerProps): ReactElement => {
    // Ensure value is a valid Dayjs object or null
    const safeValue = value && dayjs.isDayjs(value) && value.isValid() ? value : null;
    const handleChange = (newValue: Dayjs | null): void => {
        if (onChange) {
            onChange(newValue);
        }
    };
    // Handle input from Faker Filler or manual typing
    // This parses various date formats and calls onChange with a valid Dayjs object
    const handleInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>): void => {
            const inputValue = (event.target as HTMLInputElement).value;
            if (inputValue && onChange) {
                // Try parsing with multiple common date formats
                const dateFormats = [
                    'MM/DD/YYYY',
                    'M/D/YYYY',
                    'MM-DD-YYYY',
                    'YYYY-MM-DD',
                    'MMMM D, YYYY',
                    'MMM D, YYYY',
                    'D MMMM YYYY',
                    'D MMM YYYY'
                ];
                const parsedDate = dayjs(inputValue, dateFormats, true);
                if (parsedDate.isValid()) {
                    onChange(parsedDate);
                }
            }
        },
        [onChange]
    );

    return (
        <SCDatePickerWrapper>
            <DatePicker
                defaultValue={defaultValue}
                disabled={isDisabled}
                disableFuture={disableFuture}
                disablePast={disablePast}
                format={format}
                label={label}
                maxDate={maxDate}
                minDate={minDate}
                onAccept={onAccept}
                onChange={handleChange}
                onError={onError}
                readOnly={readOnly}
                value={safeValue}
                views={views}
                slotProps={{
                    textField: {
                        'aria-label': ariaLabel,
                        error: !!errorText,
                        helperText: errorText || helperText,
                        id: id,
                        inputProps: {
                            'aria-label': ariaLabel,
                            'data-testid': dataTestId,
                            onInput: handleInput
                        },
                        name: name,
                        onBlur: onBlur,
                        placeholder: placeholder,
                        required: isRequired,
                        variant: variant
                    },
                    field: {
                        clearable: clearable
                    }
                }}
            />
        </SCDatePickerWrapper>
    );
};

export { TSDatePicker };
